'use client';

import { type FieldValues, useFormState } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { ButtonStyle } from '@sola/ark-core/atoms/button-style/button-style.tsx';
import { Skeleton } from '@sola/ark-core/atoms/skeleton/skeleton.tsx';
import { Alert } from '@sola/ark-core/molecules/alert/alert.tsx';
import { Form, useFormMethods } from '@sola/ark-core/molecules/form/form.tsx';

import {
  LocalAlertsProvider,
  useLocalAlerts,
} from '../../molecules/local-alerts/local-alerts-provider.tsx';
import { LocalAlerts } from '../../molecules/local-alerts/local-alerts.tsx';
import {
  type PreferenceOption,
  PreferenceRow,
  PreferenceRowContainer,
  PreferenceSection,
} from '../../molecules/preference-row/preference-row.tsx';
import {
  SettingsErrorAlertMessage,
  createLetterWritingPreferencesUpdateSuccessAlert,
} from '../../utils/alerts.tsx';
import {
  type LetterWritingFormValues,
  WritingPreferenceChannel,
  deriveWritingPreferences,
  useLetterWritingPreferences,
  useLetterWritingPreferencesLoadState,
} from './letter-writing-preferences.logic.ts';

export const LetterWritingPreferencesSkeleton = () => (
  <div className="mt-comfy-xl" role="status" aria-busy="true">
    <PreferenceSection>
      <div className="LetterWritingPreferences__container md:flex-row md:items-center md:justify-between">
        <Skeleton height={23} width="90%" />
      </div>
      {Array.from({ length: 2 }, (_, index) => (
        <PreferenceRowContainer key={index}>
          <div className="PreferenceRow__details flex flex-col gap-snug-md pb-comfy-sm flex-1">
            <Skeleton height={20} width="60%" />
            <Skeleton height={16} count={3} />
          </div>
          <div className="px-comfy-xs">
            <Skeleton height={48} width={200} />
          </div>
        </PreferenceRowContainer>
      ))}
      <div className="flex justify-end py-comfy-lg">
        <Skeleton height={48} width={160} />
      </div>
    </PreferenceSection>
  </div>
);

const LetterWritingSaveButton = ({
  children,
  isSaving,
}: {
  children: React.ReactNode;
  isSaving: boolean;
}) => {
  const { isDirty } = useFormState();
  const isDisabled = !isDirty || isSaving;

  return (
    <ButtonStyle size="lg" isBusy={isSaving} disabled={isDisabled}>
      <button type="submit" disabled={isDisabled}>
        {children}
      </button>
    </ButtonStyle>
  );
};

const LetterWritingPreferencesForm = () => {
  const { t } = useTranslation(['my-settings']);
  const { hasEmail, hasPrint, save, isSaving } = useLetterWritingPreferences();
  const { addAlerts, clearAlerts } = useLocalAlerts();
  const formMethods = useFormMethods();

  const emailOptions: PreferenceOption[] = [
    {
      id: 'letter-email',
      label: t('my-settings:letterWriting.emailMe'),
      value: 'email',
    },
  ];

  const printOptions: PreferenceOption[] = [
    {
      id: 'letter-print',
      label: t('my-settings:letterWriting.sendPrint'),
      value: 'print',
    },
  ];

  const formDefaultValues = {
    email: { email: hasEmail },
    print: { print: hasPrint },
  };

  const handleSave = async (data: FieldValues) => {
    const writingPreferences = deriveWritingPreferences(
      data as LetterWritingFormValues,
    );
    formMethods.current?.clearErrors();
    clearAlerts();
    try {
      await save({ writingPreferences });

      formMethods.current?.reset(data);
      addAlerts(createLetterWritingPreferencesUpdateSuccessAlert());
    } catch {
      formMethods.current?.setGeneralError({
        message: (
          <SettingsErrorAlertMessage i18nKey="my-settings:letterWriting.errors.saving.message" />
        ),
      });
    }
  };

  return (
    <PreferenceSection>
      <LocalAlerts />
      <Form
        className="mt-comfy-xl"
        formMethods={formMethods}
        onSubmit={handleSave}
        options={{ defaultValues: formDefaultValues }}
      >
        <PreferenceRowContainer>
          <p className="body-xs md:body-sm body-neutral-mild">
            {t('my-settings:letterWriting.heading')}
          </p>
        </PreferenceRowContainer>
        <PreferenceRow
          fieldName={WritingPreferenceChannel.Email}
          title={t('my-settings:letterWriting.email.title')}
          description={t('my-settings:letterWriting.email.description')}
          options={emailOptions}
        />

        <PreferenceRow
          fieldName={WritingPreferenceChannel.Print}
          title={t('my-settings:letterWriting.print.title')}
          description={t('my-settings:letterWriting.print.description')}
          options={printOptions}
        />

        <div className="flex justify-end py-comfy-lg">
          <LetterWritingSaveButton isSaving={isSaving}>
            {t('my-settings:letterWriting.saveChanges')}
          </LetterWritingSaveButton>
        </div>
      </Form>
    </PreferenceSection>
  );
};

export const LetterWritingPreferences = () => {
  const { isError, showSkeleton } = useLetterWritingPreferencesLoadState();

  if (showSkeleton) {
    return <LetterWritingPreferencesSkeleton />;
  }

  if (isError) {
    return (
      <Alert
        variant="negative"
        dismissible={false}
        message={
          <SettingsErrorAlertMessage i18nKey="my-settings:letterWriting.errors.loading.message" />
        }
      />
    );
  }

  return (
    <LocalAlertsProvider>
      <LetterWritingPreferencesForm />
    </LocalAlertsProvider>
  );
};
